/**
 * Buyback-specific form state sub-hook.
 * Manages: excluded old items, brand sweetener, guardrails.
 */

import { useState, useEffect } from 'react';
import { SelectProps } from '@amzn/awsui-components-core/select';
import { BrandSweetenerOption } from 'src/@types/valueMatching/enums';
import { FormMode } from 'src/components/organisms/CampaignForm/types';
import { CampaignData } from 'src/@types/valueMatching/campaign';

export interface UseBuybackFormStateParams {
    mode: FormMode;
    initialData?: CampaignData;
}

/** Brand sweetener options — constant defined outside the component to avoid re-creation. */
const BRAND_SWEETENER_OPTIONS: SelectProps.Option[] = [
    { label: 'Yes', value: BrandSweetenerOption.YES },
    { label: 'No', value: BrandSweetenerOption.NO },
];

export function useBuybackFormState({ mode, initialData }: UseBuybackFormStateParams) {
    const [excludedOldItemIds, setExcludedOldItemIds] = useState('');
    const [brandSweetenerOption, setBrandSweetenerOption] = useState<BrandSweetenerOption>(BrandSweetenerOption.YES);
    const [buybackPercentOfAsinPrice, setBuybackPercentOfAsinPrice] = useState('');
    const [buybackPercentOfBuybackOffer, setBuybackPercentOfBuybackOffer] = useState('');
    const [buybackAbsoluteValueCap, setBuybackAbsoluteValueCap] = useState('');

    // ── Populate from initialData (modify / view) ───────────────────────
    useEffect(() => {
        if (mode === FormMode.CREATE || !initialData) return;

        const buybackAttrs = initialData.attributes?.buyback;
        if (buybackAttrs) {
            setExcludedOldItemIds(buybackAttrs.excludedOldItemIDs?.join(', ') ?? '');
            setBrandSweetenerOption(
                buybackAttrs.includeFixedValueSweetener === false
                    ? BrandSweetenerOption.NO
                    : BrandSweetenerOption.YES,
            );
        }

        const buybackGuard = initialData.guardrails?.buyback;
        if (buybackGuard) {
            setBuybackPercentOfAsinPrice(
                String(buybackGuard.percentOfASINPriceGuardrail ?? ''),
            );
            setBuybackPercentOfBuybackOffer(
                String(buybackGuard.percentOfBuybackOfferGuardrail ?? ''),
            );
            setBuybackAbsoluteValueCap(
                String(buybackGuard.absoluteValueGuardrail?.currencyValue ?? ''),
            );
        }
    }, [mode, initialData]);

    return {
        excludedOldItemIds,
        setExcludedOldItemIds,
        brandSweetenerOption,
        setBrandSweetenerOption,
        brandSweetenerOptions: BRAND_SWEETENER_OPTIONS,
        buybackPercentOfAsinPrice,
        setBuybackPercentOfAsinPrice,
        buybackPercentOfBuybackOffer,
        setBuybackPercentOfBuybackOffer,
        buybackAbsoluteValueCap,
        setBuybackAbsoluteValueCap,
    };
}
